/**
 * SupplierMultiSelect — searchable multi-select for the Part Schedule
 * Supplier quick-filter.
 *
 * A popover with a type-to-filter box over the option list and a checkbox
 * per supplier, plus a pinned "(No supplier)" option for rows whose
 * Supplier column is empty. An empty selection means "all suppliers"
 * (no filtering), matching the previous single-select's "All suppliers".
 */
import { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ChevronsUpDown, Search, X } from 'lucide-react';

/** Sentinel for rows with an empty Supplier column — matches the API. */
export const NO_SUPPLIER = '__none__';

interface SupplierMultiSelectProps {
  /** Supplier names from the options endpoint (already sorted). */
  options: string[];
  /** Currently selected names (may include NO_SUPPLIER). Empty = all. */
  selected: string[];
  /** Called with the full new selection on every toggle/clear. */
  onChange: (selected: string[]) => void;
}

export default function SupplierMultiSelect({ options, selected, onChange }: SupplierMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(name => name.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (name: string) => {
    onChange(selected.includes(name) ? selected.filter(v => v !== name) : [...selected, name]);
  };

  const label = useMemo(() => {
    if (selected.length === 0) return 'All suppliers';
    if (selected.length === 1) return selected[0] === NO_SUPPLIER ? '(No supplier)' : selected[0];
    return `${selected.length} suppliers`;
  }, [selected]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Filter by supplier"
          className="w-[220px] mt-1 justify-between font-normal"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Type to find a supplier…"
              aria-label="Search suppliers"
              className="pl-7 h-8"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
            <Checkbox
              checked={selected.includes(NO_SUPPLIER)}
              onCheckedChange={() => toggle(NO_SUPPLIER)}
              aria-label="(No supplier)"
            />
            <span className="italic">(No supplier)</span>
          </label>
          {visible.map(name => (
            <label
              key={name}
              className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm"
            >
              <Checkbox
                checked={selected.includes(name)}
                onCheckedChange={() => toggle(name)}
                aria-label={name}
              />
              <span className="truncate">{name}</span>
            </label>
          ))}
          {visible.length === 0 && (
            <div className="px-2 py-4 text-sm text-muted-foreground text-center">
              No suppliers match “{query.trim()}”
            </div>
          )}
        </div>
        {selected.length > 0 && (
          <div className="p-2 border-t flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{label}</span>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => onChange([])}>
              <X className="h-3 w-3 mr-1" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
